import { Controller } from '@hotwired/stimulus'
import { type HTMLInputEvent } from '../types/html_event'

// Connects to data-controller="sensor-map-creator"
export default class extends Controller {
  static targets = [
    'mapImage',
    'container',
    'canvas',
    'legend',
    'sensorTemplate',
    'sensors',
    'filename',
    'exports',
    'currentFileText',
    'canvasContainer'
  ]

  readonly mapImageTarget!: HTMLImageElement
  readonly containerTarget!: HTMLDivElement
  readonly canvasTarget!: SVGElement
  readonly legendTarget!: SVGGElement
  readonly sensorTemplateTarget!: SVGGElement
  readonly sensorsTarget!: SVGGElement
  readonly filenameTarget!: HTMLInputElement
  readonly exportsTarget!: HTMLDivElement
  readonly currentFileTextTarget!: HTMLElement
  readonly canvasContainerTarget!: HTMLDivElement

  declare initial_svg: string

  connect (): void {
    this.initial_svg = this.canvasTarget.outerHTML
  }

  async loadImage (event: HTMLInputEvent): Promise<void> {
    const files = event.target.files
    if (files === null) return

    this.flushSvg()

    const file = files[0]
    this.exportsTarget.style.display = 'block'
    this.currentFileTextTarget.textContent = `(${file.name})`
    this.filenameTarget.value = file.name.split('.')[0]

    const reader = new FileReader()
    if (file.type === 'image/svg+xml') {
      const fileContent = this.stripCallbacks(await file.text())
      this.loadSvg(fileContent)
    } else {
      reader.onload = this.setImageSource.bind(this)
      reader.readAsDataURL(file)
    }
  }

  loadSvg (svgContent: string): void {
    const svg = new DOMParser().parseFromString(svgContent, 'image/svg+xml')
    this.prepareSvg(svg)
    this.canvasTarget.outerHTML = svg.documentElement.outerHTML
    const sensors = this.sensorsTarget.getElementsByTagName('g')
    Array.from(sensors).forEach(sensor => {
      this.loadSensorEventListeners(sensor)
    })
    this.containerTarget.classList.remove('mapCreator-containerHidden')
  }

  flushSvg (): void {
    this.canvasTarget.outerHTML = this.initial_svg
  }

  prepareSvg (svg: Document): void {
    svg.documentElement.dataset.sensorMapCreatorTarget = 'canvas'
    const viewBox = svg.documentElement.getAttribute('viewBox')
    if (viewBox !== null && viewBox !== '') {
      svg.documentElement.removeAttribute('viewBox')
      const dimensions = viewBox.split(/\s+|,/)
      const [,, width, height] = dimensions.map(parseFloat)
      svg.documentElement.setAttribute('width', `${width}`)
      svg.documentElement.setAttribute('height', `${height}`)
    }
    const newSensorsTarget = svg.documentElement.querySelector('[data-sensor-map-creator-target="sensors"]')
    if (newSensorsTarget === null) {
      const t = document.createElement('g')
      t.dataset.sensorMapCreatorTarget = 'sensors'
      svg.documentElement.appendChild(t)
    }
  }

  addSensor (): void {
    const newSensor = this.sensorTemplateTarget.firstElementChild?.cloneNode(true) as SVGGElement
    this.sensorsTarget.appendChild(newSensor)
    this.loadSensorEventListeners(newSensor)
  }

  loadSensorEventListeners (sensor: SVGGElement): void {
    Array.from(sensor.children).forEach((element) => {
      const el = element as SVGGElement
      el.addEventListener('mousedown', this.drag.bind(this))
      el.addEventListener('touchstart', this.drag.bind(this))
    })
  }

  setImageSource (event: ProgressEvent<FileReader>): void {
    if (event.target === null) return

    const result = event.target.result?.toString()
    if (result === undefined) return

    this.mapImageTarget.setAttribute('xlink:href', result)
    this.containerTarget.classList.remove('mapCreator-containerHidden')
    this.clearSensors()
    this.calculateSVGDimensions(result)
  }

  scaleLegend (svgWidth: number, svgHeight: number): void {
    const legendBoundingBox = this.legendTarget.getBBox()
    const scale = svgWidth / legendBoundingBox.width
    const legendXPos = -legendBoundingBox.x * scale
    this.legendTarget.style.transform = `translate(${legendXPos}px, ${svgHeight + 30}px) scale(${scale})`
  }

  positionLegend (legendWidth: number, svgWidth: number, svgHeight: number): void {
    const legendHalf = legendWidth / 2
    const imgHalf = svgWidth / 2
    const legendXPos = imgHalf - legendHalf
    this.legendTarget.style.transform = `translate(${legendXPos}px, ${svgHeight + 30}px)`
  }

  calculateSVGDimensions (image: string): void {
    const img = new Image()
    img.src = image
    img.onload = () => {
      const width = img.width
      const height = img.height
      this.mapImageTarget.setAttribute('width', width.toString())
      this.mapImageTarget.setAttribute('height', height.toString())
      this.canvasTarget.setAttribute('width', width.toString())
      this.canvasTarget.setAttribute('height', (height + 70).toString())

      const legendWidth = this.legendTarget.getBoundingClientRect().width
      if (legendWidth > width) {
        this.scaleLegend(width, height)
      } else {
        this.positionLegend(legendWidth, width, height)
      }

      img.remove()
    }
  }

  clearSensors (): void {
    const sensors = this.sensorsTarget.children
    Array.from(sensors).forEach(sensor => { sensor.remove() })
  }

  getPositionFromEvent (event: MouseEvent | TouchEvent): { clientX: number, clientY: number } {
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY
    return { clientX, clientY }
  }

  drag (event: MouseEvent | TouchEvent): void {
    event.preventDefault()

    // SVG element being dragged
    const draggedElement = event.currentTarget as SVGGElement

    // The SVG group for the sensor
    const sensorElement = draggedElement.parentElement as HTMLElement

    const passingCircle = sensorElement.getElementsByTagName('circle')[0]
    const engagedCircle = sensorElement.getElementsByTagName('circle')[1]
    const sensorCircle = sensorElement.getElementsByTagName('circle')[2]

    const passingLeftRect = sensorElement.getElementsByTagName('rect')[0]
    const passingRightRect = sensorElement.getElementsByTagName('rect')[1]
    const passingTopRect = sensorElement.getElementsByTagName('rect')[2]
    const passingBottomRect = sensorElement.getElementsByTagName('rect')[3]

    const engagedLeftRect = sensorElement.getElementsByTagName('rect')[4]
    const engagedRightRect = sensorElement.getElementsByTagName('rect')[5]
    const engagedTopRect = sensorElement.getElementsByTagName('rect')[6]
    const engagedBottomRect = sensorElement.getElementsByTagName('rect')[7]

    const { clientX, clientY } = this.getPositionFromEvent(event)

    const mouseXOnDown = clientX + this.canvasContainerTarget.scrollLeft
    const mouseYOnDown = clientY + this.canvasContainerTarget.scrollTop

    const passingRadius = parseFloat(passingCircle.getAttribute('r') ?? '0')
    const engagedRadius = parseFloat(engagedCircle.getAttribute('r') ?? '0')

    // Offset from cursor to sensor CENTER (viewport coords)
    const sensorRect = sensorElement.getBoundingClientRect()
    const sensorOffsetX = clientX - (sensorRect.left + passingRadius)
    const sensorOffsetY = clientY - (sensorRect.top + passingRadius)

    // Calculate the X and Y position for the sensor in the SVG canvas coordinate space
    // using ONE coordinate system (viewport/client + container scroll + canvas rect).
    const calculateSensorPostion = (event: MouseEvent | TouchEvent): { x: number, y: number } => {
      const { clientX, clientY } = this.getPositionFromEvent(event)

      const mouseX = clientX + this.canvasContainerTarget.scrollLeft - sensorOffsetX
      const mouseY = clientY + this.canvasContainerTarget.scrollTop - sensorOffsetY

      const canvasRect = this.canvasTarget.getBoundingClientRect()

      return {
        x: mouseX - canvasRect.left,
        y: mouseY - canvasRect.top
      }
    }

    const moveSensor = (event: MouseEvent | TouchEvent): void => {
      const actualPostions = calculateSensorPostion(event)

      passingCircle.setAttribute('cx', actualPostions.x.toString())
      passingCircle.setAttribute('cy', actualPostions.y.toString())
      engagedCircle.setAttribute('cx', actualPostions.x.toString())
      engagedCircle.setAttribute('cy', actualPostions.y.toString())
      sensorCircle.setAttribute('cx', actualPostions.x.toString())
      sensorCircle.setAttribute('cy', actualPostions.y.toString())

      moveResizeRects(actualPostions.x, actualPostions.y, engagedRadius, 'engaged')
      moveResizeRects(actualPostions.x, actualPostions.y, passingRadius, 'passing')
    }

    const dropSensor = (): void => {
      document.removeEventListener('mousemove', moveSensor)
      document.removeEventListener('mouseup', dropSensor)
      document.removeEventListener('touchmove', moveSensor)
      document.removeEventListener('touchend', dropSensor)
    }

    const calculateMinRadius = (type: 'engaged' | 'passing'): number => {
      if (type === 'engaged') {
        return 5
      } else {
        return engagedRadius + 5
      }
    }

    const calculateMaxRadius = (type: 'engaged' | 'passing'): number => {
      if (type === 'engaged') {
        return passingRadius - 5
      } else {
        return Infinity
      }
    }

    const resizeSensor = (event: MouseEvent | TouchEvent): void => {
      const { target } = draggedElement.dataset
      if (target !== 'engaged' && target !== 'passing') return
      const leftRect = target === 'engaged' ? engagedLeftRect : passingLeftRect
      const rightRect = target === 'engaged' ? engagedRightRect : passingRightRect
      const initialRadius = target === 'engaged' ? engagedRadius : passingRadius
      const circle = target === 'engaged' ? engagedCircle : passingCircle
      const minRadius = calculateMinRadius(target)
      const maxRadius = calculateMaxRadius(target)

      const { clientX, clientY } = this.getPositionFromEvent(event)
      const mouseX = clientX + this.canvasContainerTarget.scrollLeft
      const mouseY = clientY + this.canvasContainerTarget.scrollTop

      const isHorizontal = draggedElement === leftRect || draggedElement === rightRect

      let pixelDiff = 0
      if (isHorizontal) {
        pixelDiff = (mouseX - mouseXOnDown) * resizeDirection()
      } else {
        pixelDiff = (mouseY - mouseYOnDown) * resizeDirection()
      }

      const newRadius = initialRadius + pixelDiff

      if (newRadius > minRadius && newRadius < maxRadius) {
        circle.setAttribute('r', newRadius.toString())

        const circleCx = parseFloat(circle.getAttribute('cx') ?? '0')
        const circleCy = parseFloat(circle.getAttribute('cy') ?? '0')
        moveResizeRects(circleCx, circleCy, newRadius, target)
      }
    }

    const stopResize = (): void => {
      document.removeEventListener('mousemove', resizeSensor)
      document.removeEventListener('mouseup', stopResize)
      document.removeEventListener('touchmove', resizeSensor)
      document.removeEventListener('touchend', stopResize)
    }

    // Either 0, 1 or -1
    const resizeDirection = (): number => {
      return parseFloat(draggedElement.dataset.direction ?? '0')
    }

    const moveResizeRects = (circleCx: number, circleCy: number, radius: number, type: 'passing' | 'engaged'): void => {
      const leftRect = type === 'engaged' ? engagedLeftRect : passingLeftRect
      const rightRect = type === 'engaged' ? engagedRightRect : passingRightRect
      const topRect = type === 'engaged' ? engagedTopRect : passingTopRect
      const bottomRect = type === 'engaged' ? engagedBottomRect : passingBottomRect

      leftRect.setAttribute('x', (circleCx - radius - leftRect.width.baseVal.value / 2).toString())
      leftRect.setAttribute('y', (circleCy - leftRect.height.baseVal.value / 2).toString())

      rightRect.setAttribute('x', (circleCx + radius - rightRect.width.baseVal.value / 2).toString())
      rightRect.setAttribute('y', (circleCy - rightRect.height.baseVal.value / 2).toString())

      topRect.setAttribute('x', (circleCx - topRect.width.baseVal.value / 2).toString())
      topRect.setAttribute('y', (circleCy - radius - topRect.height.baseVal.value / 2).toString())

      bottomRect.setAttribute('x', (circleCx - bottomRect.width.baseVal.value / 2).toString())
      bottomRect.setAttribute('y', (circleCy + radius - bottomRect.height.baseVal.value / 2).toString())
    }

    // Detect whether the sensor is being resized or moved
    if (resizeDirection() !== 0) {
      document.addEventListener('mousemove', resizeSensor)
      document.addEventListener('mouseup', stopResize)
      document.addEventListener('touchmove', resizeSensor)
      document.addEventListener('touchend', stopResize)
    } else {
      document.addEventListener('mousemove', moveSensor)
      document.addEventListener('mouseup', dropSensor)
      document.addEventListener('touchmove', moveSensor)
      document.addEventListener('touchend', dropSensor)
    }
  }

  showHowTo (): void {
    const howToModalContent = document.getElementById('howToModalContent')
    if (howToModalContent !== null) {
      howToModalContent.classList.remove('mapCreator-slideOut')
      howToModalContent.classList.add('mapCreator-slideIn')
    }

    const howToModal = document.getElementById('howToModal')
    if (howToModal !== null) {
      howToModal.classList.remove('mapCreator-fadeOut')
      howToModal.classList.add('mapCreator-fadeIn')
    }
  }

  hideHowTo (event: MouseEvent): void {
    if (event.target !== event.currentTarget) { return }

    const howToModalContent = document.getElementById('howToModalContent')
    if (howToModalContent !== null) {
      howToModalContent.classList.remove('mapCreator-slideIn')
      howToModalContent.classList.add('mapCreator-slideOut')
    }

    const howToModal = document.getElementById('howToModal')
    if (howToModal !== null) {
      howToModal.classList.remove('mapCreator-fadeIn')
      howToModal.classList.add('mapCreator-fadeOut')
    }
  }

  svgBlobUrl (): string {
    const serializer = new XMLSerializer()
    const svg = serializer.serializeToString(this.canvasTarget)

    const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
    return URL.createObjectURL(svgBlob)
  }

  downloadBlob (url: string, defaultFileName: string): void {
    const downloadLink = document.createElement('a')
    downloadLink.href = url
    const filename = this.filenameTarget.value !== '' ? this.filenameTarget.value : defaultFileName
    downloadLink.download = filename

    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
  }

  downloadImage (): void {
    const url = this.svgBlobUrl()

    const img = new Image()
    img.src = url
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height

      const ctx = canvas.getContext('2d')
      ctx?.drawImage(img, 0, 0)

      const dataURL = canvas.toDataURL('image/png')
      this.downloadBlob(dataURL, 'Sensor Map.png')

      URL.revokeObjectURL(url)

      img.remove()
    }
  }

  exportSvg (): void {
    const url = this.svgBlobUrl()
    this.downloadBlob(url, 'Sensor Map.svg')
    URL.revokeObjectURL(url)
  }

  // The DOMParser class will automatically ignore any script tags in the SVG content,
  // however it does not account for callbacks e.g. <img src='' onerror='alert('error')'>
  stripCallbacks (svgContent: string): string {
    return svgContent.replace(/\s*on\w+="[^"]*"/g, '')
  }
}
